import React from 'react';
import { ReviewAnswer } from './ReviewList';
import HarbourIcon from '../harbour/HarbourIcon';
import HarbourNotice from '../harbour/HarbourNotice';
import HarbourStatusChip from '../harbour/HarbourStatusChip';

/**
 * HarbourReviewList
 * The Harbour "Review your answers" card (TDI-53 frames 3254:157693 school,
 * 3454:13643 parent or carer): the form's questions numbered in order under
 * their "Part N of M" headings, each row with the answer and a link to change
 * it ("Answer" while unanswered). The row being changed is tinted and carries
 * an arrow; the row just changed says "Answer updated".
 *
 * readOnly (answers locked by confirm and submit, TDI-415) drops every link:
 * offering a change the server would refuse would not be honest.
 */

function isUnanswered(step, answers) {
  if (step.type === 'group') {
    return (step.children || []).every((child) => isEmptyValue(answers[child.questionId]));
  }
  return isEmptyValue(answers[step.questionId]);
}

function isEmptyValue(v) {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return String(v).trim() === '';
}

export default function HarbourReviewList({
  steps = [],
  answers = {},
  eyebrow = null,
  tone = 'sen',
  notice = null,
  readOnly = false,
  selectedIndex = null,
  updatedIndex = null,
  compact = false,
  titleRef = null,
  onSelect,
}) {
  // Group by section ("Part N of M: Title"), numbering questions across the form.
  const sectionTitles = [];
  steps.forEach((s) => {
    const title = s.sectionTitle || '';
    if (!sectionTitles.includes(title)) sectionTitles.push(title);
  });
  const partHeading = (title) => {
    if (sectionTitles.length < 2) return title;
    const n = sectionTitles.indexOf(title) + 1;
    return title ? `Part ${n} of ${sectionTitles.length}: ${title}` : `Part ${n} of ${sectionTitles.length}`;
  };
  const groups = [];
  steps.forEach((s, i) => {
    const title = s.sectionTitle || '';
    const last = groups[groups.length - 1];
    if (!last || last.title !== title) groups.push({ title, items: [{ step: s, index: i }] });
    else last.items.push({ step: s, index: i });
  });

  return (
    <div className={`harbour-review${compact ? ' harbour-review--compact' : ''}`}>
      {eyebrow && (
        <p className={`harbour-review__eyebrow harbour-review__eyebrow--${tone}`}>
          <span className="harbour-review__dot" aria-hidden="true" />
          {eyebrow}
        </p>
      )}
      <h1 ref={titleRef} tabIndex={-1} className="harbour-review__title">Review your answers</h1>
      {notice && !readOnly && (
        <HarbourNotice kind="info" title="No need to check every answer" className="harbour-review__notice">
          {notice}
        </HarbourNotice>
      )}
      {groups.map((g, gi) => (
        <section key={`${g.title}_${gi}`} className="harbour-review__part" aria-label={partHeading(g.title) || undefined}>
          {g.title && <h2 className="harbour-review__part-title">{partHeading(g.title)}</h2>}
          <ol className="harbour-review__rows">
            {g.items.map(({ step, index }) => {
              const unanswered = isUnanswered(step, answers);
              const selected = selectedIndex === index;
              const actionLabel = unanswered ? 'Answer' : 'Change';
              return (
                <li
                  key={step.questionId}
                  className={`harbour-review__row${selected ? ' harbour-review__row--selected' : ''}`}
                >
                  <span className="harbour-review__number">{index + 1}</span>
                  <span className="harbour-review__question">{step.label}</span>
                  <span className="harbour-review__answer">
                    {unanswered ? 'No answer given' : <ReviewAnswer step={step} answers={answers} />}
                    {updatedIndex === index && !selected && (
                      <HarbourStatusChip status="done" tone={tone} label="Answer updated" />
                    )}
                  </span>
                  <span className="harbour-review__action">
                    {!readOnly && (selected ? (
                      <HarbourIcon name="arrow-right" size={24} />
                    ) : (
                      <button
                        type="button"
                        className="harbour-review__link"
                        onClick={() => onSelect?.(index)}
                        aria-label={`${actionLabel}: ${step.label}`}
                      >
                        {actionLabel}
                      </button>
                    ))}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
